import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import { setFilters } from 'actions/search';
import { getFilter, getSearchView, getActiveFilters } from 'selectors/search';
import { countries, states } from 'services/inputTokens';
import { Icon, Tooltip } from 'components/common';
import { OrganisationSearch } from 'components/Search/Results/helpers';
import { tradeshows, sessionTypes, roleTypes } from 'services/inputTokens';
import { colours } from 'utils/theme';

import {
  Filter,
  helpText,
  calculateDate,
  showTradeshowWarning,
} from '../helpers';
import OrgFilters from './OrgTradeshows';
import FilterGroup from './FilterGroup';
import TradeshowFlagsWarning from './TradeshowFlagsWarning';
import {
  ATTENDING_PRESENTER_ROLES,
  isMissingTradeshowFlag,
} from 'reducers/tradeshowFilterRules';
import { tradeshowOrganisationKeys, tradeshowResearcherKeys } from './keys';
import { getResearcherText } from 'utils/constants';
import { tagRender } from 'components/common/TagRender';
import { renderOption } from 'components/common/OptionRender';

const Title = ({ tradeshows, showValidation }) => {
  const date = useSelector(getFilter('date'));
  const [from, to] = calculateDate(date);
  const showWarning = date && showTradeshowWarning(from, to, tradeshows);

  if (showWarning) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'unset',
          flex: 1,
        }}
      >
        Tradeshows
        <Tooltip
          defaultOpen
          placement='right'
          overlayStyle={{ maxWidth: 350 }}
          title={
            <p>
              One of the selected tradeshows falls outside the date range for
              your search.
              <br />
              <br />
              To see the results for this tradeshow select "Custom" from the
              date dropdown above the search results and extend the date to
              include the tradeshow.
            </p>
          }
        >
          <div style={{ marginLeft: 5 }}>
            <Icon icon='error' size='sm' color={colours.orange} />
          </div>
        </Tooltip>
      </div>
    );
  }

  if (showValidation) {
    return <TradeshowFlagsWarning />;
  }

  return 'Tradeshows';
};

const Tradeshows = ({ loading }) => {
  const dispatch = useDispatch();
  const tradeshowValue = useSelector(getFilter('tradeshow'));
  const orgCheckBoxSelected = useSelector(getFilter('exhibitingShow'));
  const presentingSelected = useSelector(getFilter('presentingAtTradeshow'));
  const thisShowSelected = useSelector(getFilter('thisShowOnly'));
  const thisRoleTypeOnlyValue = useSelector(getFilter('thisRoleTypeOnly'));
  const thisSessionTypeOnlyValue = useSelector(
    getFilter('thisSessionTypeOnly')
  );
  const attendingPresentersOnlyValue = useSelector(
    getFilter('attendingPresentersOnly')
  );

  const sessionTypeValue = useSelector(getFilter('sessionType'));
  const roleTypeValue = useSelector(getFilter('roleType'));

  const prevLensRef = useRef({
    sessionType: sessionTypeValue?.length ?? 0,
    roleType: roleTypeValue?.length ?? 0,
  });

  const isOrgView = OrganisationSearch(useSelector(getSearchView));

  const activeFilters = useSelector(
    getActiveFilters([
      ...Object.keys(tradeshowResearcherKeys),
      ...Object.keys(tradeshowOrganisationKeys),
    ])
  );

  const showValidationValue = isMissingTradeshowFlag({
    tradeshow: tradeshowValue,
    exhibitingShow: orgCheckBoxSelected,
    presentingAtTradeshow: presentingSelected,
    thisShowOnly: thisShowSelected,
  });

  const disableThisTradeshowOnly =
    orgCheckBoxSelected && presentingSelected && isOrgView;

  const checkboxLabel =
    tradeshowValue?.length > 1
      ? 'Search selected tradeshows only'
      : 'Search selected tradeshow only';

  const sessionTypeCheckboxValue =
    sessionTypeValue?.length > 1
      ? 'Only show results with selected session types'
      : 'Only show results with selected session type';

  const roleTypeCheckboxValue =
    roleTypeValue?.length > 1
      ? 'Only show results with selected roles'
      : 'Only show results with selected role';

  const handlePresenters = value => {
    if (value) {
      dispatch(
        setFilters({
          attendingPresentersOnly: true,
          thisRoleTypeOnly: true,
          roleType: ATTENDING_PRESENTER_ROLES,
        })
      );
    } else {
      dispatch(
        setFilters({
          attendingPresentersOnly: false,
          thisRoleTypeOnly: false,
          roleType: [],
        })
      );
    }
  };

  useEffect(() => {
    if (
      !roleTypeValue ||
      roleTypeValue.length === 0 ||
      !thisRoleTypeOnlyValue
    ) {
      if (attendingPresentersOnlyValue) {
        dispatch(setFilters({ attendingPresentersOnly: false }));
      }
      return;
    }

    const isPresenterOnlySelection = ATTENDING_PRESENTER_ROLES.every(role =>
      roleTypeValue.includes(role)
    );

    if (attendingPresentersOnlyValue && !isPresenterOnlySelection) {
      dispatch(
        setFilters({
          attendingPresentersOnly: false,
        })
      );
    } else if (!attendingPresentersOnlyValue && isPresenterOnlySelection) {
      dispatch(
        setFilters({
          attendingPresentersOnly: true,
        })
      );
    }
  }, [
    roleTypeValue,
    attendingPresentersOnlyValue,
    thisRoleTypeOnlyValue,
    dispatch,
  ]);

  useEffect(() => {
    const prev = prevLensRef.current;

    const nextTradeshowLen = tradeshowValue?.length ?? 0;
    const nextSessionLen = sessionTypeValue?.length ?? 0;
    const nextRoleLen = roleTypeValue?.length ?? 0;

    const updates = {};

    const thisShowOnlyKey = tradeshowResearcherKeys.thisShowOnly.key;
    const thisSessionOnlyKey = tradeshowResearcherKeys.thisSessionTypeOnly.key;
    const thisRoleOnlyKey = tradeshowResearcherKeys.thisRoleTypeOnly.key;
    const attendingPresentersOnlyKey =
      tradeshowResearcherKeys.attendingPresentersOnly.key;

    // Tradeshows -> thisShowOnly
    // The first-selection default lives in the search reducer's SET_FILTER branch, for the
    // same reason as the role default below. Clearing stays here.
    if (nextTradeshowLen === 0 && thisShowSelected) {
      updates[thisShowOnlyKey] = false;
    }

    // Session -> thisSessionTypeOnly
    if (
      prev.sessionType === 0 &&
      nextSessionLen > 0 &&
      !thisSessionTypeOnlyValue
    ) {
      updates[thisSessionOnlyKey] = true;
    }
    if (nextSessionLen === 0 && thisSessionTypeOnlyValue) {
      updates[thisSessionOnlyKey] = false;
    }

    // Role -> thisRoleTypeOnly
    // The first-selection default lives in the search reducer's SET_FILTER branch, not here.
    // This effect only sees the store, so it cannot tell a user picking a role from SciAI
    // Search applying a whole filter set - and SET_FILTERS strips an explicit false, so it
    // used to overwrite the AI's deliberate broad reading with true. Clearing stays here.
    if (nextRoleLen === 0 && thisRoleTypeOnlyValue) {
      updates[thisRoleOnlyKey] = false;
      updates[attendingPresentersOnlyKey] = false;
    }

    if (Object.keys(updates).length > 0 && !loading) {
      dispatch(setFilters(updates));
    }

    prevLensRef.current = {
      sessionType: nextSessionLen,
      roleType: nextRoleLen,
    };
  }, [
    tradeshowValue,
    sessionTypeValue,
    roleTypeValue,
    thisShowSelected,
    thisSessionTypeOnlyValue,
    thisRoleTypeOnlyValue,
    loading,
    dispatch,
  ]);

  return (
    <FilterGroup
      icon='image'
      title={
        <Title
          tradeshows={tradeshowValue}
          showValidation={showValidationValue}
        />
      }
      activeKeys={activeFilters}
      sectionKey='tradeshows'
    >
      <>
        <Filter
          filterType='selectIncludeExclude'
          placeholder='Tradeshow Names'
          mode={'multiple'}
          style={{ width: '100%' }}
          tagRender={tagRender}
          filterId={tradeshowResearcherKeys.tradeshow.key}
          options={tradeshows}
          disabledKey='dataNotAvailable'
          disabledTooltipText='Data not available yet'
          helpText={`Filter results by tradeshows that ${getResearcherText({
            isPlural: true,
          })} have attended / will be attending`}
          disabled={loading}
          optionProp={{ optionLabel: 'label', optionValue: 'label' }}
        />
        <OrgFilters
          filterId={[
            tradeshowOrganisationKeys.exhibitingShow.key,
            tradeshowOrganisationKeys.presentingAtTradeshow.key,
          ]}
          loading={loading}
        />
        <Tooltip
          title={helpText.thisShowOnly}
          mouseEnterDelay={0.5}
          placement='right'
          overlayStyle={{ minWidth: 450 }}
          filterId='thisShowOnly'
          disabled={loading}
        >
          <Filter
            filterType='checkbox'
            filterId={tradeshowResearcherKeys.thisShowOnly.key}
            disabled={
              !tradeshowValue ||
              tradeshowValue.length === 0 ||
              disableThisTradeshowOnly ||
              loading
            }
            labelText={checkboxLabel}
          />
        </Tooltip>
        <Filter
          filterType='selectIncludeExclude'
          mode={'multiple'}
          style={{ width: '100%' }}
          disabled={loading}
          renderOption={renderOption}
          tagRender={tagRender}
          placeholder='Tradeshow Session Type'
          filterId={tradeshowResearcherKeys.sessionType.key}
          helpText={`Filter results by Session Types that ${getResearcherText({
            isPlural: true,
          })} have participated in`}
          options={sessionTypes}
          remoteSearchOnClick
        />
        <Tooltip
          title={helpText.thisSessionTypeOnly}
          placement='right'
          overlayStyle={{ maxWidth: 450 }}
        >
          <Filter
            filterType='checkbox'
            filterId={tradeshowResearcherKeys.thisSessionTypeOnly.key}
            disabled={
              !sessionTypeValue || sessionTypeValue.length === 0 || loading
            }
            labelText={sessionTypeCheckboxValue}
          />
        </Tooltip>
        <Filter
          filterType='selectIncludeExclude'
          mode={'multiple'}
          style={{ width: '100%' }}
          disabled={loading}
          renderOption={renderOption}
          tagRender={tagRender}
          placeholder='Tradeshow Role'
          filterId={tradeshowResearcherKeys.roleType.key}
          helpText={`Filter results by Tradeshow Roles that ${getResearcherText(
            {
              isPlural: true,
            }
          )} have performed`}
          options={roleTypes}
          remoteSearchOnClick
        />
        <Tooltip
          title={helpText.thisRoleTypeOnly}
          placement='right'
          overlayStyle={{ maxWidth: 450 }}
        >
          <Filter
            filterType='checkbox'
            filterId={tradeshowResearcherKeys.thisRoleTypeOnly.key}
            disabled={!roleTypeValue || roleTypeValue.length === 0 || loading}
            labelText={roleTypeCheckboxValue}
          />
        </Tooltip>
        <Tooltip
          title={helpText.attendingPresentersOnly}
          placement='right'
          overlayStyle={{ maxWidth: 450 }}
        >
          <div>
            <Filter
              filterType='switch'
              filterId={tradeshowResearcherKeys.attendingPresentersOnly.key}
              onChange={handlePresenters}
              disabled={loading}
            >
              {tradeshowResearcherKeys.attendingPresentersOnly.description}
            </Filter>
          </div>
        </Tooltip>
        <>
          <div style={{ width: 'fit-content', cursor: 'default' }}>
            {'Tradeshow Location'}
          </div>
          <Filter
            filterType='selectIncludeExclude'
            mode={'multiple'}
            style={{ width: '100%' }}
            renderOption={renderOption}
            tagRender={tagRender}
            filterId={tradeshowResearcherKeys.tradeshowCountryRegion.key}
            helpText={helpText.filterByTradeshowCountryRegion}
            placeholder={
              tradeshowResearcherKeys.tradeshowCountryRegion.description
            }
            options={countries}
            disabled={loading}
          />
          <Filter
            filterType='selectIncludeExclude'
            mode={'multiple'}
            style={{ width: '100%' }}
            tagRender={tagRender}
            placeholder={tradeshowResearcherKeys.tradeshowState.description}
            filterId={tradeshowResearcherKeys.tradeshowState.key}
            helpText={helpText.filterByTradeshowState}
            options={states}
            remoteSearchOnClick
            optionProp={{
              optionLabel: 'displayName',
              optionValue: 'id',
            }}
            disabled={loading}
          />
          <Filter
            filterType='token'
            placeholder={tradeshowResearcherKeys.tradeshowCity.description}
            filterId={tradeshowResearcherKeys.tradeshowCity.key}
            helpText={helpText.filterByTradeshowCity}
            disabled={loading}
            includeExclude
          />
        </>
      </>
    </FilterGroup>
  );
};

Tradeshows.propTypes = {
  loading: PropTypes.bool,
};

export default Tradeshows;
